import { Capacitor, registerPlugin } from "@capacitor/core";
import { useGame } from "../game/store";

export type AppleAuthResult = {
  ok: boolean;
  /** Stable code or verbose native error (includes ASAuthorizationError raw value). */
  reason?: string;
};

type NativeAppleAuthResponse = {
  response: {
    user: string;
    email?: string | null;
    givenName?: string | null;
    familyName?: string | null;
    identityToken?: string | null;
    authorizationCode?: string | null;
  };
};

type NativeAppleAuthPlugin = {
  authorize(options?: {
    /** Space-separated. Empty / omit = identity only (recommended). */
    scopes?: string;
    nonce?: string;
    state?: string;
  }): Promise<NativeAppleAuthResponse>;
};

/** App-target iOS plugin — see ios/App/App/NativeAppleAuthPlugin.swift */
const NativeAppleAuth = registerPlugin<NativeAppleAuthPlugin>("NativeAppleAuth");

/**
 * Map Apple / plugin errors for Settings UI.
 * Preserve ASAuthorizationError code + native detail so TF diagnosis can tell
 * user-cancel (1001) from Apple backend "Sign Up Not Completed" (also often 1001).
 */
function mapAppleFailure(raw: unknown): string {
  const msg = raw instanceof Error ? raw.message : String(raw ?? "");
  const lower = msg.toLowerCase();
  const codeMatch = msg.match(/:(\d{3,4})\]/);
  const code = codeMatch ? Number(codeMatch[1]) : NaN;
  const codeTag = Number.isFinite(code) ? `appleErr=${code}` : "appleErr=?";
  const snippet = msg.slice(0, 180);

  if (
    /not completed/i.test(msg) ||
    /non termin/i.test(lower) ||
    /inscription/i.test(lower)
  ) {
    return `incomplete ${codeTag} ${snippet}`;
  }

  if (
    code === 1001 ||
    /cancel/i.test(msg) ||
    /annul/i.test(lower) ||
    /abort/i.test(lower)
  ) {
    // Apple often reports incomplete SIWA as canceled+empty userInfo — keep detail.
    if (/userInfoKeys=\(empty\)/i.test(msg)) {
      return `incomplete ${codeTag} ${snippet}`;
    }
    return `cancelled ${codeTag} ${snippet}`;
  }

  if (code === 1000 || code === 1004 || /unknown/i.test(lower) || /failed/i.test(lower)) {
    return `incomplete ${codeTag} ${snippet}`;
  }

  return `${codeTag} ${snippet || "error"}`;
}

/**
 * Sign in with Apple — binds leaderboard identity to the Apple subject.
 * Web: mock link so we can test the Settings UX in browser.
 * Native: App-target NativeAppleAuth (dedicated host UIViewController).
 */
export async function signInWithApple(): Promise<AppleAuthResult> {
  if (!Capacitor.isNativePlatform()) {
    const mockId = `web_apple_${Date.now().toString(36)}`;
    useGame.getState().linkAppleAccount(mockId, "Courier");
    return { ok: true, reason: "web-mock" };
  }

  try {
    // Identity only first — rules out name/email consent sheet failures.
    // Native host VC starts ASAuthorizationController in viewDidAppear.
    const res = await NativeAppleAuth.authorize({
      scopes: "name email",
    });
    const user = res.response?.user;
    if (!user || user.length < 8) {
      return { ok: false, reason: "no_user" };
    }
    const given = res.response?.givenName?.trim();
    const family = res.response?.familyName?.trim();
    const suggested = [given, family].filter(Boolean).join(" ").slice(0, 16);
    useGame.getState().linkAppleAccount(user, suggested || undefined);
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: mapAppleFailure(e) };
  }
}
